function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    let det = event.currentTarget.closest('details');
    if (det) {
      event.currentTarget.setAttribute('aria-expanded', !det.hasAttribute('open'));
    }
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
    if (this.getAttribute('data-autoplay') === 'true') {
      this.loadContent(false);
    }
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
 
    this.isVertical = this.slider.classList.contains('slider--vertical'); // Custom
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;

    if (!this.isVertical) {
      this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    } else {
      this.sliderItemOffset = this.sliderItemsToShow[1].offsetTop - this.sliderItemsToShow[0].offsetTop;
    }

    if (!this.isVertical) {
      this.slidesPerPage = Math.floor(
        (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
      );
    } else {
      this.slidesPerPage = Math.floor(
        (this.slider.clientHeight - this.sliderItemsToShow[0].offsetTop) / this.sliderItemOffset
      );
    }

    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (!this.isVertical && this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else if (this.isVertical && this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollTop === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    if (this.isVertical) {
      const lastVisibleSlide = this.slider.clientHeight + this.slider.scrollTop - offset;
      return element.offsetTop + element.clientHeight <= lastVisibleSlide && element.offsetTop >= this.slider.scrollTop;
    }
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    if (!this.isVertical) {
      this.slideScrollPosition =
        event.currentTarget.name === 'next'
          ? this.slider.scrollLeft + step * this.sliderItemOffset
          : this.slider.scrollLeft - step * this.sliderItemOffset;
    } else {
      this.slideScrollPosition =
        event.currentTarget.name === 'next'
          ? this.slider.scrollTop + step * this.sliderItemOffset
          : this.slider.scrollTop - step * this.sliderItemOffset;
    }
    // console.log((this.isVertical ? 'V' : 'H'), ' / this.slideScrollPosition = ', this.slideScrollPosition, ' (', event.currentTarget.name, ' / ', this.slider.scrollTop,' + ', step ,' * ', this.sliderItemOffset,')');
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    if (!this.isVertical) {
      this.slider.scrollTo({
        left: position,
      });
    } else {
      this.slider.scrollTo({
        top: position,
      });
    }
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    // console.log('[SlideshowComponent.init] ', this);
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      (this.reducedMotion && this.reducedMotion.matches) || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    // console.log(event);
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = (this.currentPage === this.sliderItemsToShow.length || this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1], -10));
    // console.log('[onButtonClick] [',event.currentTarget.name,'] this.isSlideVisible=', this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1], -10),
    //             ' / this.currentPage=', this.currentPage, ' / isFirstSlide=', isFirstSlide, ' / isLastSlide=', isLastSlide, ' / this.sliderItemsToShow.length=', this.sliderItemsToShow.length);

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider && this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      if (target && target.name == 'size_convert') {
        return;
      }
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    // console.log('[ProductRecommendations.initializeRecommendations] productId = ', productId);
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    // console.log('[ProductRecommendations.loadRecommendations] productId = ', productId);
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }

        if (html.querySelector('.glide__slides')) {
          this.initSlider();
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }

  initSlider() {
    this.classList.remove('glide--loading');
    new Glide('#' + this.id, {
        type: 'carousel',
        startAt: 0,
        perView: 4,
        gap: 0,
        autoplay: false,
        // hoverpause: true,
        touchRatio: 1.0,
        peek: 0,
        breakpoints: {
          2000: {
            perView: 4
          },
          750: {
            perView: 3
          },
          480: {
            perView: 2
          }
        }
    }).mount();
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}


// CUSTOM

class CartSummaryDiscountForm extends HTMLElement {
  constructor() {
    // console.log('[CartSummaryDiscountForm]');
    super();
    this.discount_code = {
      code: '',
      message: '',
      success: false,
      loading: false,
      valid: true
    };

    this.discount_code.code = this.getCookie('discount_code');
    // console.log('this.discount_code.code = ', this.discount_code.code);
    if (typeof(this.discount_code.code) === 'undefined' || !this.discount_code.code) {
      this.discount_code.code = '';
    }
    this.submitButton = this.querySelector('.cart-discount__apply-discount');
    this.inputField = this.querySelector('input.cart-discount__discount-input');
    this.appliedText = this.querySelector('.cart-discount__applied');
    this.discountInfo = this.querySelector('.cart-summary__discount-info');
    this.inputField.value = this.discount_code.code;

    this.AtcCodes = [
      {
        code: 'FREEICONCAP',
        product: '39307316232264'
      }
    ];
    this.collectionMap = [];
    this.querySelectorAll('.collection-map span').forEach((elem) => {
      this.collectionMap.push(
        {
          collection_id: elem.getAttribute('data-collection-id'),
          product_id: elem.getAttribute('data-product-id')
        }
      );
    });

    this.setupEvents();
    this.updateView();
    this.applyExisitingDiscountCode(() => {
      if (!this.discount_code.valid && this.discount_code.code) {
        document.cookie = 'discount_code=; expires=Thu, 01 Jan 1970 00:00:01 GMT;'; // clear the cookie
      }
    });
  }

  updateView() {
    this.discountInfo.innerHTML = this.discount_code.message;
    this.discountInfo.classList.toggle('hidden', (this.discount_code.message ? false : true));
    this.discountInfo.classList.toggle('invalid', (this.discount_code.valid ? false : true));
    this.appliedText.classList.toggle('hidden', (this.discount_code.valid && this.discount_code.code ? false : true));
    this.appliedText.querySelector('span').innerText = this.discount_code.code;
  }

  getCookie(name) {
      var value = "; " + document.cookie;
      var parts = value.split('; '+name+'=');
      if (parts.length == 2) {
        return parts.pop().split(";").shift();
      }
      return null;
  }

  setupEvents() {
    this.querySelector('form').addEventListener('submit', this.applyDiscount.bind(this));
    this.querySelector('input.cart-discount__discount-input').addEventListener('change', this.inputChange.bind(this));
    this.appliedText.querySelector('button').addEventListener('click', this.resetDiscountCode.bind(this));
    this.querySelector('.cart-discount__input-clear').addEventListener('click', () => {
      this.querySelector('input.cart-discount__discount-input').value = '';
    });
    // console.log('this.discount_code = ', this.discount_code);
  }

  inputChange(e) {
    this.discount_code.code = this.inputField.value;
    console.log('this.discount_code.code = ', this.discount_code.code);
  }

  applyDiscount(e) {
    e.preventDefault();
    console.log('this.discount_code = ', this.discount_code);
    if (this.discount_code.code) {
      this.discount_code.loading = true;
      this.submitButton.disabled = 'disabled';
      this.submitButton.innerText = 'Checking...';
      // console.log('redirect to = ', '/discount/' + this.discount_code.code + '?redirect=' + window.routes.cart_url);
      window.location.replace(routes.root_url + (routes.root_url != '/' ? '/' : '') + 'discount/' + this.discount_code.code + '?redirect=' + window.routes.cart_url);
    }
  }

  applyExisitingDiscountCode(callbackFunc) {
      let dcode = this.getCookie('discount_code');
      if(dcode) {
          this.discount_code.code = dcode;
          this.applyDiscountToCart(callbackFunc);
      }
  }

  resetDiscountCode() {
      this.discount_code = {
          code: '',
          message: '',
          success: false,
          loading: false
      }
      document.cookie = 'discount_code=; expires=Thu, 01 Jan 1970 00:00:01 GMT;';
      // this.loading = true;
      window.location.reload();
  }

  applyDiscountToCart(callbackFunc) {
      const discount_code = this.discount_code.code;
      this.discount_code.loading = true;
      fetch('https://yiff6eo4ia.execute-api.ap-southeast-2.amazonaws.com/prod/discounts/code?value=' + discount_code, {
              headers: { "x-api-key": "J7ILmBENGz2aVSoUqQ4w17feCniTnGnQ8qDxLxfx" },
              redirect: 'follow'
          })
          .then(res => res.json())
          .then(res => {
              // console.log(res);
              this.discount_code_atc();
              this.discount_code.loading = false;
              this.discount_code.valid = true;
              this.updateView();
              // this.loading = false;
              this.discount_code.success = res.success;
              if(res.success) {
                  this.discount_code.message = "Discount will be applied at checkout";
                  this.updateView();

                  const {customer_selection, target_selection, 
                      allocation_method, value, value_type, 
                      prerequisite_subtotal_range, usage_limit,
                      ends_at, prerequisite_collection_ids,
                      prerequisite_to_entitlement_quantity_ratio,
                      prerequisite_customer_ids,
                      prerequisite_saved_search_ids,
                      prerequisite_quantity_range,
                      once_per_customer} = res;

                  let prerequisites_met = true;

                  if(prerequisite_subtotal_range && prerequisite_subtotal_range.greater_than_or_equal_to) {
                      if((this.cart.original_total_price / 100) <= prerequisite_subtotal_range.greater_than_or_equal_to) {
                          prerequisites_met = false;
                      }
                  }

                  if(prerequisite_quantity_range && prerequisite_quantity_range.greater_than_or_equal_to) {
                      if(this.cart.item_count < prerequisite_quantity_range.greater_than_or_equal_to) {
                          prerequisites_met = false;
                      }
                  }

                  let product_ids_used_for_prerequisite = [];
                  let num_prerequisite = 0;
                  let count = 0;

                  if(prerequisite_collection_ids.length) {
                      prerequisites_met = false;
                      const cMaps = this.collectionMap.filter(collectionMap => prerequisite_collection_ids.includes(collectionMap.collection_id));

                      // console.log('cMaps:', cMaps);
                      
                      this.cart.items.forEach(item => {
                          if(cMaps.length) {
                              for (i = 0; i < cMaps.length; ++i) {
                                  if(item.product_id == cMaps[i].product_id) {
                                      product_ids_used_for_prerequisite.push(item.product_id);
                                      num_prerequisite = num_prerequisite + item.quantity;
                                      count = count + item.quantity;
                                  }
                                  // console.log('count:', count);
                              }
                          }
                      })
                      // console.log(count, prerequisite_to_entitlement_quantity_ratio.prerequisite_quantity, prerequisite_to_entitlement_quantity_ratio.entitled_quantity);
                      prerequisites_met = (count >= (prerequisite_to_entitlement_quantity_ratio.prerequisite_quantity + prerequisite_to_entitlement_quantity_ratio.entitled_quantity)) && this.cart.item_count >= prerequisite_to_entitlement_quantity_ratio.prerequisite_quantity + prerequisite_to_entitlement_quantity_ratio.entitled_quantity;
                  }

                  const now = new Date();
                  const expires_at = new Date(ends_at);
                  // console.log(now, expires_at);
                  has_expired = ends_at != null && now > expires_at;
                  if(has_expired) {
                      prerequisites_met = false;
                      // console.log('expired');
                      this.discount_code.message = "Discount code has expired";
                      this.discount_code.valid = false;
                      this.updateView();
                  }

                  // console.log('prereq', prerequisites_met);
                  // console.log(product_ids_used_for_prerequisite);

                  if(prerequisites_met) {
                      if(customer_selection == 'all') {
                          // console.log('customer_selection', customer_selection);
                          if(target_selection == 'all') {
                              // console.log('target_selection', target_selection);
                              // console.log('allocation_method:', allocation_method);
                              if(allocation_method == 'across') {
                                  this.rePriceTotal(value_type, value);
                                  // console.log('break');
                                  if(usage_limit || once_per_customer) {
                                      this.discount_code.message = `Usage limits apply.<br>Navigate to checkout to verify validity`
                                  }
                                  return;
                              } else {
                                  // allocation_method = 'each' //
                                  this.cart.items.forEach(item => this.rePriceItem(item, value_type, value));
                              }
                          } else {
                              // target_selection = 'entitled' //
                              // console.log('target_selection', target_selection); 
                              // console.log('allocation_method:', allocation_method);      
                              const {entitled_collection_ids, entitled_product_ids, entitled_variant_ids, prerequisite_to_entitlement_purchase} = res;

                              let quantity_to_discount = Math.floor(prerequisite_to_entitlement_quantity_ratio.entitled_quantity * (count / (prerequisite_to_entitlement_quantity_ratio.entitled_quantity + prerequisite_to_entitlement_quantity_ratio.prerequisite_quantity)));
                              let total_quantity_to_discount = quantity_to_discount;

                              // console.log(entitled_collection_ids);

                              if(entitled_collection_ids.length) {
                                  for (const cMap of this.collectionMap) {
                                      if(entitled_collection_ids.includes(cMap.collection_id)) {
                                          for(i=0;i<this.cart.items.length;i++) {
                                              // console.log(this.cart.items[i].product_id, cMap.product_id);
                                              if(cMap && this.cart.items[i].product_id == cMap.product_id) {
                                                  if(allocation_method == 'across' && value_type != 'percentage') {
                                                      this.rePriceItem(this.cart.items[i], value_type, value / this.cart.item_count, total_quantity_to_discount);
                                                  } else {
                                                      this.rePriceItem(this.cart.items[i], value_type, value, total_quantity_to_discount);
                                                  }
                                                  if(product_ids_used_for_prerequisite.includes(this.cart.items[i].product_id) && this.cart.items[i].quantity == 1) total_quantity_to_discount = total_quantity_to_discount - 1;
                                              }
                                          }
                                      }
                                  };
                              }

                              // console.log('entitled_product_ids:', entitled_product_ids);
                              // console.log('num_prerequisite', num_prerequisite);
                              // console.log('num_entitled', num_entitled);

                              if(entitled_product_ids.length) {
                                  this.cart.items.forEach(item => {
                                      // console.log(item.title);
                                      if(entitled_product_ids.includes(item.product_id)) {
                                          const spend_minus_free_entitled_product = this.cart.total_price - item.price;
                                          if(spend_minus_free_entitled_product > (prerequisite_to_entitlement_purchase.prerequisite_amount * 100) && prerequisite_to_entitlement_quantity_ratio.entitled_quantity == 1) {
                                              this.rePriceItem(item, 'percentage', '-100.0', 1);
                                          } else {
                                              let quantity_to_discount = Math.floor(num_prerequisite / (prerequisite_to_entitlement_quantity_ratio.entitled_quantity / prerequisite_to_entitlement_quantity_ratio.prerequisite_quantity + prerequisite_to_entitlement_quantity_ratio.entitled_quantity));
                                              // console.log('quantity_to_discount:',quantity_to_discount);
                                              // if(product_ids_used_for_prerequisite.includes(item.product_id) && item.quantity == 1) quantity_to_discount = 0;
                                              // console.log(quantity_to_discount);
                                              this.rePriceItem(item, value_type, value, quantity_to_discount);
                                              // console.log(item.line_price);
                                          }   
                                      }
                                  });
                              }
                          }
                      } else {
                          // customer_selection = 'prerequisite' //
                          if(prerequisite_customer_ids.length > 0 || prerequisite_saved_search_ids.length > 0) {
                              this.discount_code.message = "Discount code restricted to select customers.<br><br>Navigate to checkout to view discount (if applicable)";
                          }
                      }
                  }

                  if(!has_expired && prerequisite_customer_ids.length == 0 && prerequisite_saved_search_ids.length == 0) {
                      if(usage_limit || once_per_customer) {
                          this.discount_code.message = `Usage limits apply.<br>Navigate to checkout to verify validity.`
                      }
                  }
                      
                  var total = 0;
                  for (i = 0; i < this.cart.items.length; ++i) {
                      total += this.cart.items[i].line_price;
                  }
                  this.cart.total_price = total;
                  this.updateView();

              } else {
                  this.discount_code.message = "Discount code is invalid. Please try again";
                  this.discount_code.valid = false;
                  this.updateView();
              }

              if (typeof(callbackFunc) == 'function') {
                callbackFunc();
              }
          })
  }

  discount_code_atc() {
      let discount_atcs = [];

      this.AtcCodes.forEach(rule => {
          const applied_discount_code = this.discount_code.code.toLowerCase();
          const rule_code = rule.code.toLowerCase();
          if(applied_discount_code == rule_code) {
              discount_atcs.push(rule.product);
          }
      });

      if(discount_atcs.length > 0) {
          const variants_already_in_cart = this.cart.items.map(item => item.id);
          const items_array = discount_atcs.map(discount_atc => {
              if(!variants_already_in_cart.includes(Number(discount_atc))) {
                  return {
                      id: discount_atc,
                      quantity: 1
                  }
              }
          });
          fetch(window.routes.cart_add_url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: items_array
          }).then((resp) => {
            resp.json().then((res) => {
              setTimeout(() => {
                window.location.href = window.routes.cart_url;
              }, 10);
            });
          });
          // $.ajax({
          //         type: 'POST', 
          //         url: window.routes.cart_add_url,
          //         dataType: 'json', 
          //         data: {
          //             items: items_array
          //         },
          //         success: function() {
          //             window.location.href = window.routes.cart_url;
          //         }
          // });
      }
  }

  rePriceTotal(type, value, quantity_to_discount) {
      // value = Number(value);
      // if(type == 'percentage') {
      //     this.cart.total_price = this.cart.total_price * ((100+value)/100);
      // } else {
      //     quantity_to_discount = quantity_to_discount || 1;
      //     // console.log(quantity_to_discount);
      //     this.cart.total_price = (Math.abs(value*quantity_to_discount*100) < this.cart.total_price) ? this.cart.total_price + (value*quantity_to_discount*100) : 0;
      // }
  }

  rePriceItem(item, type, value, quantity_to_discount) {
      // value = Number(value);
      // if(type == 'percentage') {
      //     if(quantity_to_discount && quantity_to_discount < item.quantity) value = value / (item.quantity / quantity_to_discount);
      //     if(quantity_to_discount == null || isNaN(quantity_to_discount)) {
      //         item.line_price = item.line_price * ((100+value)/100);
      //     } else {
      //         if(quantity_to_discount > 0) item.line_price = item.line_price * ((100+value)/100);
      //     }
      // } else {
      //     // value = (quantity_to_discount) ? value * quantity_to_discount : value * item.quantity;
      //     value = value * item.quantity;
      //     item.line_price = (Math.abs(value*100) < item.line_price) ? item.line_price + (value*100) : 0;
      // }
  }

}
customElements.define('cart-summary-discount-form', CartSummaryDiscountForm);

function variantSelectorClickEvent(e) {

  if (e.target.classList.contains('variant-atc') && e.target.classList.contains('available') && e.target.getAttribute('data-variant-id')) {
    let elem = e.target;
    let varId = elem.getAttribute('data-variant-id');
    let prodId = elem.getAttribute('data-product-id');
    let sideCart = document.querySelector('cart-drawer');
    elem.classList.add('loading');

    // Add item to cart

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('form_type', 'product');
    if (sideCart) {
      formData.append('sections', sideCart.getSectionsToRender().map((section) => section.id));
      sideCart.setActiveElement(document.activeElement);
    }
    formData.append('sections_url', window.location.pathname);
    formData.append('product-id', prodId);
    formData.append('id', varId);
    formData.append('quantity', 1);

    config.body = formData;
    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          console.error(response.description);
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            errors: response.errors || response.description,
            message: response.message,
          });
          return;
        } else {
          // window.location = window.routes.cart_url;
          // return;
        }

        let parentPopup = elem.closest('.product-variant-popup');
        if (parentPopup) {
          parentPopup.classList.remove('open');
        }

        if (document.querySelector('.ais-page')) {
          const aisHitItem = (parentPopup ? parentPopup.closest('.ais-rw-hit') : document.querySelector('.ais-page .ais-rw-hit[data-product-id="' + prodId + '"]'));
          if (aisHitItem && typeof(logAlgoliaAtcEvent) !== 'undefined') {
            logAlgoliaAtcEvent(elem, aisHitItem, response); // currently custom web pixel does this, but with less data
          }
          // console.log('[adding item to cart] elem=', elem, ' / parentPopup=', parentPopup, ' / aisHitItem=', aisHitItem);
        }

        let openMobileSelector = document.querySelector('.mobile-atc-selector--open');
        if (openMobileSelector) {
          openMobileSelector.classList.remove('mobile-atc-selector--open');
        }
        if (!document.body.classList.contains('cart-drawer-open')) {
          document.body.classList.remove('overflow-hidden');
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'product-form',
          productVariantId: formData.get('id'),
          cartData: response,
        });

        if (sideCart) {
          sideCart.renderContents(response);
        }

      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        // this.submitButton.classList.remove('loading');
        if (sideCart && sideCart.classList.contains('is-empty')) { sideCart.classList.remove('is-empty'); }
        // if (!this.error) this.submitButton.removeAttribute('aria-disabled');
        // this.querySelector('.loading__spinner').classList.add('hidden');
        elem.classList.remove('loading');
      });

  }
}

(function() {

  // Sold-out message and back-in-stock popup

  if (document.querySelector('.product-form__sold-message .rw-button')) {
    document.querySelector('.product-form__sold-message .rw-button').addEventListener('click', (e) => {
      if (typeof(BISPopover) !== 'undefined') {
        let variantId = document.querySelector('.product-form input[name=id]').value;
        BISPopover.show({variantId});
      } else {
        document.querySelector('.product-form__sold-message .rw-button').classList.add('hidden');
      }
    });
    if (document.querySelector('.product__info-container variant-selects')) {
      document.querySelector('.product__info-container').addEventListener('click', (e) => {
        if (e.target.classList.contains('tooltiptext')) {
          e.target.parentNode.click();
          let variantId = e.target.getAttribute('data-variant-id');
          if (typeof(BISPopover) !== 'undefined' && typeof(variantId) !== 'undefined' && variantId) {
            BISPopover.show({variantId});
          }
        }
      });
    }
  }

  // Mobile tabs

  document.querySelectorAll('.mobile-collections-bar-tabs .mobile-collections-bar-tab').forEach((elem) => {
    elem.addEventListener('click', (e) => {
      e.preventDefault();
      let href = '' + e.target.href, tabNum = 1;
      if (href.indexOf('#tab2') >= 0) {
        tabNum = 2;
      }
      document.querySelector('.mobile-collections-bar').classList.toggle('mobile-collections-bar--view-1', (tabNum == 1));
      document.querySelector('.mobile-collections-bar').classList.toggle('mobile-collections-bar--view-2', (tabNum == 2));
      let activeTab = document.querySelector('.mobile-collections-bar-tab--active');
      if (activeTab) {
        activeTab.classList.remove('mobile-collections-bar-tab--active');
      }
      e.target.classList.add('mobile-collections-bar-tab--active');
    });
  });


  // Mobile search

  if (document.querySelector('#header-mobile-search')) {
    document.querySelector('#header-mobile-search').addEventListener('focus', (e) => {
      document.querySelector('.header .header__mobile-search').classList.add('header__mobile-search--open');
      document.body.classList.add('overflow-hidden');
    });
    document.querySelector('#header-mobile-search').addEventListener('blur', (e) => {
      document.querySelector('.header .header__mobile-search').classList.remove('header__mobile-search--open');
      if (!document.body.classList.contains('cart-drawer-open')) {
        document.body.classList.remove('overflow-hidden');
      }
    });
    document.querySelector('#header-mobile-search').addEventListener('keyup', (e) => {
      if (e.keyCode == 27) {
        document.querySelector('#header-mobile-search').blur();
      }
    });
    document.querySelector('.mobile-search-overlay').addEventListener('click', (e) => {
      document.querySelector('.header .header__mobile-search').classList.remove('header__mobile-search--open');
      if (!document.body.classList.contains('cart-drawer-open')) {
        document.body.classList.remove('overflow-hidden');
      }
    });
  }


  // Hash links

  let scrollLinkOnClick = function(e) {
    elem = e.target;
    if (elem.tagName != 'A' && elem.parentNode && elem.parentNode.tagName == 'A') {
      elem = elem.parentNode;
    }
    if (elem.tagName != 'A') {
      return;
    }

    let href = '' + elem.href;
    if (href.indexOf('#') >= 0) {
      href = href.substring(href.indexOf('#'), href.length);
      let destElem = document.querySelector(href);
      if (destElem && (destElem.tagName == 'SECTION' || destElem.closest('section'))) {
        if (destElem.tagName != 'SECTION') {
          destElem = destElem.closest('section');
        }
        window.scrollTo({ top: destElem.offsetTop, behavior: 'smooth' })
      }
      e.preventDefault();
    }
  };

  document.querySelectorAll('.rich-text .rich-text__blocks .rich-text__text a').forEach((elem) => {
    let href = '' + elem.href;
    if (href.substring(0, 1) == '#' || href.indexOf(window.location.href) >= 0) {
      elem.addEventListener('click', scrollLinkOnClick);
    }
  });

  // Footer section collapse on mobile

  document.querySelectorAll('.footer-block__heading').forEach((elem) => {
    elem.addEventListener('click', (e) => {
      let contentSection = e.target.parentNode;
      if (!contentSection.classList.contains('footer-block')) {
        contentSection = contentSection.closest('.footer-block');
        if (!contentSection) {
          return;
        }
      }
      if (contentSection) {
        contentSection.classList.toggle('footer-block--open');
      }
    });
  });

  // Mobile ATC menu

  if (document.querySelector('#mobile-atc-selector')) {
    document.querySelector('#mobile-atc-selector').addEventListener('click', (e) => {
        if (e.target.classList.contains('mobile-atc-selector')) {
            // console.log('[MOBILE-ATC] overlay click');
            document.querySelector('#mobile-atc-selector').classList.remove('mobile-atc-selector--open');
            if (!document.body.classList.contains('cart-drawer-open')) {
              document.body.classList.remove('overflow-hidden');
            }
            return;
        }
    });
  }

  // Variant ATC selectors

  if (document.querySelector('.product-variant-popup') || document.querySelector('product-recommendations') || document.querySelector('.ais-page') || document.querySelector('.product__model-also-wearing') || document.querySelector('cart-drawer-related-products')) {
    document.querySelectorAll('.product-grid, #mobile-atc-selector, product-recommendations.related-products, .algolia-recommended-products-section, .product__model-also-wearing').forEach((elem) => {
      if (!elem.classList.contains('.related-products') || !elem.querySelector('.product-grid')) {
        elem.addEventListener('click', variantSelectorClickEvent);
      }
    });
  }

  // Watch window resizing, to remove scroll lock on body when needed

  const thumbSlider = document.querySelector('.product media-gallery slider-component.thumbnail-slider');
  let currentWindowWidth = document.body.clientWidth;
  let lastWindowWidth = currentWindowWidth;
  window.addEventListener('resize', (e) => {
    currentWindowWidth = document.body.clientWidth;
    // console.log('[RESIZE] ', lastWindowWidth, ' => ', currentWindowWidth);

    if (lastWindowWidth < 750 && currentWindowWidth >= 750 && (document.querySelector('#mobile-atc-selector').classList.contains('mobile-atc-selector--open') || document.querySelector('.header .header__mobile-search--open'))) {
      if (document.querySelector('#mobile-atc-selector')) {
        document.querySelector('#mobile-atc-selector').classList.remove('mobile-atc-selector--open');
      }
      document.body.classList.remove('overflow-hidden'); // allow scrolling again
    }

    if (thumbSlider) {
      if ((lastWindowWidth < 900 && currentWindowWidth >= 900) || (lastWindowWidth >= 900 && currentWindowWidth < 900)) {
        thumbSlider.slider.classList.toggle('slider--vertical', (currentWindowWidth >= 900));
        thumbSlider.resetPages();
      }
    }

    lastWindowWidth = currentWindowWidth;
  });

  setTimeout(() => {
    if (thumbSlider && currentWindowWidth < 900) {
      thumbSlider.slider.classList.toggle('slider--vertical', false);
      thumbSlider.resetPages();
    }
  }, 50);

})();

// Collection list slider

function initCollectionListSlider(elementId)
{
  // console.log('[initCollectionListSlider] ', elementId);
  if (!document.querySelector(elementId)) {
    return;
  }
  document.querySelector(elementId).style.maxHeight = '';
  document.querySelector(elementId).style.overflow = '';

  if (document.querySelector(elementId + ' .collection-list--loading')) {
    document.querySelector(elementId + ' .collection-list--loading').classList.remove('collection-list--loading');
  }

  if (typeof(Glide) === 'undefined') {
    console.error('Glide JS plugin not found!');
    return;
  }

  new Glide(elementId, {
      type: 'carousel',
      startAt: 0,
      perView: 4,
      gap: 15,
      autoplay: false,
      // hoverpause: true,
      touchRatio: 1.0,
      peek: 0,
      breakpoints: {
          1287: {
              perView: 3
          },
          749: {
          },
          // 500: {
          //     perView: 1,
          //     peek: 50
          // }
      }
  }).mount();
}

// Add-to-cart selector

function showAddToCartSelector(productId, buttonElement)
{
  let variantSelector;
  let mobileAtcSelector = document.querySelector('#mobile-atc-selector');
  if (buttonElement) {
    try {
      variantSelector = buttonElement.closest('.product-variant-popup');
    } catch(err) { console.error(err); }
  }
  if (!variantSelector) {
    variantSelector = document.querySelector('#product-variant-popup-' + productId);
  }

  if (document.body.clientWidth < 750 && mobileAtcSelector) {
    mobileAtcSelector.querySelector('.mobile-atc-selector__heading').innerHTML = variantSelector.querySelector('.product-variant-popup__heading').innerHTML;
    mobileAtcSelector.querySelector('ul').innerHTML = variantSelector.querySelector('ul').innerHTML;
    mobileAtcSelector.classList.add('mobile-atc-selector--open');
    document.body.classList.add('overflow-hidden');
  } else {
    variantSelector.classList.toggle('open');
  }
}

// Algolia events

function logAlgoliaAtcEvent(buttonElement, parentContainer, atcResponse)
{
  if (!buttonElement || typeof(AlgoliaAnalytics) === 'undefined') {
    return;
  }

  if (typeof(parentContainer) === 'undefined' || !parentContainer) {
    parentContainer = buttonElement.closest('.ais-rw-hit');
  }

  // console.log('[logAlgoliaAtcEvent] buttonElement=', buttonElement, ' / parentContainer=', parentContainer);

  if (parentContainer && typeof(window.aa) !== 'undefined') {
    try {
      if (AlgoliaAnalytics.default && !AlgoliaAnalytics.default._apiKey) {
        // AlgoliaAnalytics.default.init({ appId: 'PD6OLXANCH', apiKey: '7c4b4ab00dbf89fb15b8065b0400f7a0' });
        window.aa('init', { appId: 'PD6OLXANCH', apiKey: '7c4b4ab00dbf89fb15b8065b0400f7a0' });
      }
      let eventData = {
        eventName: 'Add to cart',
        index: parentContainer.getAttribute('data-algolia-index'),
        queryID: parentContainer.getAttribute('data-algolia-queryid'),
        objectIDs: [parentContainer.getAttribute('data-algolia-objectid')],
        productId: parentContainer.getAttribute('data-product-id'),
        objectData: [{
          price: (atcResponse && typeof(atcResponse.final_price) !== 'undefined' ? (atcResponse.final_price / 100) : parseFloat(buttonElement.getAttribute('data-price'))),
          discount: (atcResponse && typeof(atcResponse.line_level_total_discount) !== 'undefined' ? (atcResponse.line_level_total_discount / 100) : 0.00),
          quantity: 1
        }],
        value: (atcResponse && typeof(atcResponse.line_price) !== 'undefined' ? (atcResponse.line_price / 100) : parseFloat(buttonElement.getAttribute('data-price'))),
        currency: algoliaShopify.config.store_currency,
      };
      if (AlgoliaAnalytics.default) {
        eventData['userToken'] = AlgoliaAnalytics.default._userToken;
      }
      // algoliaShopify.saveForConversionTracking(eventData);
      window.aa('addedToCartObjectIDsAfterSearch', eventData);
    } catch (err) {
      console.error(err);
    }
  }
}

function logAlgoliaWishlistEvent(variantId, productId, wishlistAction)
{
  if (typeof(AlgoliaAnalytics) === 'undefined') {
    return;
  }

  let listProd = document.querySelector('.ais-rw-hit[data-product-id="' + productId + '"]'); //[data-variant-id="' + variantId + '"]');

  // console.log('[logAlgoliaWishlistEvent] ', variantId, productId, wishlistAction, listProd);

  if (listProd && typeof(window.aa) !== 'undefined') {
    try {
      if (AlgoliaAnalytics.default && !AlgoliaAnalytics.default._apiKey) {
        // AlgoliaAnalytics.default.init({ appId: 'PD6OLXANCH', apiKey: '7c4b4ab00dbf89fb15b8065b0400f7a0' });
        window.aa('init', { appId: 'PD6OLXANCH', apiKey: '7c4b4ab00dbf89fb15b8065b0400f7a0' });
      }
      let eventData = {
        eventName: (wishlistAction == 'added' ? 'Add to wishlist' : 'Remove from wishlist'),
        index: listProd.getAttribute('data-algolia-index'),
        queryID: listProd.getAttribute('data-algolia-queryid'),
        positions: [parseInt(listProd.getAttribute('data-algolia-position'))],
        objectIDs: ['' + variantId],
        productId: '' + productId,
      };
      if (AlgoliaAnalytics.default) {
        eventData['userToken'] = AlgoliaAnalytics.default._userToken;
        // let result = AlgoliaAnalytics.default.clickedObjectIDsAfterSearch(eventData);
        // let result = algoliaShopify.saveForConversionTracking(eventData);
        let result = window.aa('clickedObjectIDsAfterSearch', eventData);
        // console.log('[event] result = ', result, ' / eventData = ', eventData);
      } else {
        console.error('[event] fail: AlgoliaAnalytics.default is null');
      }
    } catch (err) {
      console.error(err);
    }
  }
}


function buildVariantSelectorFromAlgolia(algoliaSearchIndex, clickedButton, prodId, popupUL, variantSelector)
{
    clickedButton.classList.add('loading');

    // algoliaSearchClient.searchSingleIndex({ // V5
    //   indexName: 'shopify_products_recently_ordered_count_desc', 
    //   searchParams: {
    //     tagFilters: ['id:' + prodId],
    //     distinct: false,
    //     hitsPerPage: 100,
    //     attributesToRetrieve: ['objectID','options', 'option_names', 'variant_title', 'inventory_quantity', 'inventory_available']
    //   }
    algoliaSearchIndex.search('', {
        tagFilters: ['id:' + prodId],
        hitsPerPage: 100,
        distinct: false,
        clickAnalytics: false,
        attributesToRetrieve: ['objectID', 'options', 'option_names', 'variant_title', 'inventory_available', 'price']
    }).then((resp) => {

        // console.log(resp.hits);
        if (typeof(resp.hits) !== 'undefined' && resp.hits) {
            aisSortVarList(resp.hits);
            for (let i in resp.hits) {
                // console.log('| ', resp.hits[i].variant_title, ' / ', resp.hits[i].objectID);
                let li = document.createElement('li');

                let var_title = '';
                if (typeof(resp.hits[i].options.size) !== 'undefined') {
                    var_title = resp.hits[i].options.size;
                }
                if (typeof(resp.hits[i].options.width) !== 'undefined') {
                    var_title = var_title + (var_title ? ' - ' : '') + resp.hits[i].options.width;
                }
                if (typeof(resp.hits[i].options.length) !== 'undefined') {
                    var_title = var_title + (var_title ? ' - ' : '') + resp.hits[i].options.length;
                }

                if (!var_title) {
                    var_title = resp.hits[i].variant_title;
                }

                li.innerHTML = 
                '<div class="variant-atc ' + (resp.hits[i].inventory_available ? '' : 'un') + 'available" data-variant-id="' + resp.hits[i].objectID + '" data-price="' + resp.hits[i].price + '" data-product-id="' + prodId + '">'+
                    '<span class="variant-atc__title">' + var_title + (resp.hits[i].inventory_available ? '' : ' - Sold Out') + '</span>' +
                    '<span class="variant-atc__adding">Adding...</span>' +
                '</div>';
                popupUL.append(li);
                li.addEventListener('click', variantSelectorClickEvent);
            }
        }

        algoliaShowTheVariantSelector(variantSelector);
        clickedButton.classList.remove('loading');

    });
}
